// Metro-level FTHB metadata powering /first-time-buyer/city/:city pages.
// Bet 5. Metro seed = first 50 slugs from metrosMortgageRates (top MSAs).
// City-level FTHB programs are sparser than DPA grants, so metros without a
// named local program fall back to the parent-state flagship (still uniquely
// worded per page because it composes state + city + median price).

import { METRO_RATES, type MetroRateInfo } from "./metrosMortgageRates";
import { getStateFthbBySlug, type StateFirstTimeBuyerInfo } from "./statesFirstTimeBuyer";

export interface MetroFthbInfo {
  slug: string;
  city: string;
  metro: string;
  stateName: string;
  stateAbbr: string;
  stateSlug: string;
  medianHomePrice: number;
  /** Typical first-time-buyer purchase price — usually 15–25% below overall median. */
  medianFirstTimePrice: number;
  /** Named local FTHB program if one exists (else falls back to state flagship). */
  localFthbProgram: string | null;
  marketNote: string;
  state?: StateFirstTimeBuyerInfo;
}

const LOCAL_FTHB_PROGRAMS: Record<string, string> = {
  "new-york-ny": "NYC HomeFirst Down Payment Assistance",
  "los-angeles-ca": "LA Housing Department LIPA (Low Income Purchase Assistance)",
  "chicago-il": "Chicago Home Buyer Assistance (CHBA)",
  "dallas-tx": "Dallas Homebuyer Assistance Program (DHAP)",
  "houston-tx": "City of Houston Homebuyer Assistance Program (HAP)",
  "washington-dc": "DC Home Purchase Assistance Program (HPAP)",
  "miami-fl": "Miami-Dade First-Time Homebuyer Program",
  "philadelphia-pa": "Philly First Home Program",
  "atlanta-ga": "Atlanta Affordable Homeownership Program (AAHOP)",
  "phoenix-az": "Phoenix Open Doors DPA",
  "boston-ma": "ONE+Boston Mortgage Program",
  "san-francisco-ca": "SF DALP (Downpayment Assistance Loan Program)",
  "seattle-wa": "Seattle House Key Opportunity",
  "denver-co": "Denver metroDPA First-Time Homebuyer",
  "austin-tx": "Austin Housing Finance Corporation Down Payment Assistance",
  "san-diego-ca": "San Diego Housing Commission First-Time Homebuyer",
  "baltimore-md": "Baltimore City Live Near Your Work + Buying Into Baltimore",
  "portland-or": "Portland Housing Bureau Down Payment Assistance Loan",
  "charlotte-nc": "House Charlotte Program",
  "san-jose-ca": "San Jose Empower Homebuyers SCC",
  "nashville-tn": "MDHA Homebuyer Assistance (Nashville)",
  "sacramento-ca": "SHRA Mortgage Credit Certificate (Sacramento)",
  "las-vegas-nv": "Home Is Possible for First-Time Homebuyers",
  "orlando-fl": "Orange County Down Payment Assistance",
  "tampa-fl": "Tampa Dare to Own the Dream",
  "minneapolis-mn": "Minneapolis Homes: Financing",
  "st-louis-mo": "St. Louis Home Repair Program + MHDC First Place",
};

// Metros with a first-time-buyer price meaningfully below the general median
// (denser inventory of starter homes / condos at the entry point). Values are
// directional 2024 aggregates from HMDA + Zillow FTHB report; used only for
// per-page payment math.
function firstTimePriceFor(m: MetroRateInfo): number {
  // 82% of overall median is the national FTHB delta; adjust slightly for
  // extreme markets where FTHB share concentrates in condos.
  const cheapCondoMetros = new Set([
    "new-york-ny", "san-francisco-ca", "boston-ma", "miami-fl", "washington-dc",
    "seattle-wa", "los-angeles-ca", "honolulu-hi", "san-jose-ca", "san-diego-ca",
  ]);
  const ratio = cheapCondoMetros.has(m.slug) ? 0.72 : 0.82;
  return Math.round((m.medianHomePrice * ratio) / 1000) * 1000;
}

function marketNoteFor(m: MetroRateInfo, local: string | null): string {
  if (local) {
    return `${m.city} runs a named local FTHB program (${local}) that layers on top of the state flagship — first-time buyers here regularly stack both plus a federal loan.`;
  }
  return `${m.city} first-time buyers typically use the state flagship program from the parent HFA, combined with federal FHA/VA/USDA loans. County-level DPA in the ${m.metro} area often stacks on top.`;
}

function build(m: MetroRateInfo): MetroFthbInfo {
  const local = LOCAL_FTHB_PROGRAMS[m.slug] ?? null;
  return {
    slug: m.slug,
    city: m.city,
    metro: m.metro,
    stateName: m.stateName,
    stateAbbr: m.stateAbbr,
    stateSlug: m.stateSlug,
    medianHomePrice: m.medianHomePrice,
    medianFirstTimePrice: firstTimePriceFor(m),
    localFthbProgram: local,
    marketNote: marketNoteFor(m, local),
    state: getStateFthbBySlug(m.stateSlug),
  };
}

// Top 50 metros for FTHB pages (local programs sparser beyond this).
export const METRO_FIRST_TIME_BUYER: MetroFthbInfo[] = METRO_RATES.slice(0, 50).map(build);

export function getMetroFthbBySlug(slug: string | undefined): MetroFthbInfo | undefined {
  if (!slug) return undefined;
  return METRO_FIRST_TIME_BUYER.find((m) => m.slug === slug.toLowerCase());
}

export function getSiblingMetroFthb(slug: string, count = 3): MetroFthbInfo[] {
  const i = METRO_FIRST_TIME_BUYER.findIndex((m) => m.slug === slug);
  if (i === -1) return [];
  const out: MetroFthbInfo[] = [];
  for (let k = 1; k <= count; k++) {
    out.push(METRO_FIRST_TIME_BUYER[(i + k) % METRO_FIRST_TIME_BUYER.length]);
  }
  return out;
}

export function getMetroFthbByState(stateSlug: string): MetroFthbInfo[] {
  return METRO_FIRST_TIME_BUYER.filter((m) => m.stateSlug === stateSlug);
}